import { ArrowRight, Lock } from "lucide-react";
import { Link } from "react-router-dom";

import { VerdictProof } from "@/components/hero/VerdictProof";
import { AppEntryLink } from "@/components/navigation/AppEntryLink";

interface HeroProps {
  // Retained for backwards compatibility with the Landing wrapper —
  // the editorial hero does not render a "scroll for more" cue.
  onScrollNext?: () => void;
}

export function Hero(_props: HeroProps) {
  return (
    <section
      className="relative mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-16 overflow-hidden px-6 pb-24 pt-20 max-[980px]:gap-10 max-[980px]:pb-20 max-[980px]:pt-14 sm:px-8 min-[981px]:grid-cols-[1.05fr_0.95fr]"
      aria-label="Introduction"
    >
      <div
        className="lp-ambient pointer-events-none absolute inset-0 z-0 overflow-hidden"
        aria-hidden
      />
      <div className="lp-grain pointer-events-none absolute inset-[-40px] z-[1]" aria-hidden />

      <div className="relative z-[2] max-w-[540px]">
        <span className="eyebrow">For patients who were told no</span>

        <h1 className="mb-7 mt-7 font-serif text-[58px] font-normal leading-[1.04] tracking-[-0.022em] text-ink max-[980px]:text-[40px]">
          They said no<span className="text-brick">.</span>
          <br />
          <em className="italic">The math says appeal.</em>
        </h1>

        <p className="mb-9 max-w-[480px] text-[16.5px] leading-[1.65] text-ink-soft">
          Upload your denial letter and we&apos;ll tell you, in twenty minutes, whether the policy
          your insurer cited actually applies — and how likely an appeal is to win. Free to look. Pay
          only for the letter.
        </p>

        <div className="mb-11 flex flex-wrap items-center gap-3.5">
          <AppEntryLink
            to="/session"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-[22px] py-[13px] text-[14px] font-medium text-parchment transition hover:-translate-y-px hover:bg-black"
          >
            Read my denial
            <ArrowRight size={16} aria-hidden />
          </AppEntryLink>
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-1.5 border-b-[0.5px] border-ink-soft px-1 py-[13px] text-[14px] text-ink-soft transition hover:text-ink"
          >
            How it works
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-[18px] text-[11px] uppercase tracking-[0.08em] text-ink-muted">
          <span className="inline-flex items-center gap-1.5">
            <Lock size={13} strokeWidth={1.75} aria-hidden />
            HIPAA-aware
          </span>
          <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-[#C0BFB8]" />
          <span>No account</span>
          <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-[#C0BFB8]" />
          <span>Purged in 24 hours</span>
        </div>
      </div>

      <VerdictProof />
    </section>
  );
}
